import { useStorageUpload } from "@stardeck-customer-apps/storage-sdk/react";
import { GripVertical, ImagePlus, Loader2, Star, Trash2 } from "lucide-react";
import * as React from "react";

import { cn } from "@/lib/utils";

import { Button } from "./button";

export interface GalleryImage {
  id?: number;
  url: string;
  sort_order: number;
  is_primary: boolean;
}

interface GalleryUploadProps {
  images: GalleryImage[];
  onChange: (images: GalleryImage[]) => void;
  maxImages?: number;
  className?: string;
}

export function GalleryUpload({ images, onChange, maxImages = 10, className }: GalleryUploadProps) {
  const fileInputRef = React.useRef<HTMLInputElement>(null);
  const [uploadingCount, setUploadingCount] = React.useState(0);
  const [draggedIndex, setDraggedIndex] = React.useState<number | null>(null);

  const { mutate: upload } = useStorageUpload({
    onSuccess: (result) => {
      const newImage: GalleryImage = {
        url: result.url,
        sort_order: images.length,
        is_primary: images.length === 0, // First image is primary by default
      };
      onChange([...images, newImage]);
      setUploadingCount((prev) => prev - 1);
    },
    onError: (error) => {
      console.error("Upload failed:", error);
      setUploadingCount((prev) => prev - 1);
    },
  });

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (!files) return;

    const remainingSlots = maxImages - images.length;
    const filesToUpload = Array.from(files).slice(0, remainingSlots);

    setUploadingCount(filesToUpload.length);

    filesToUpload.forEach((file) => {
      upload({
        file,
        options: {
          isPublic: true,
          metadata: { type: "vehicle-gallery" },
        },
      });
    });

    // Reset input
    e.target.value = "";
  };

  const handleRemove = (index: number) => {
    const newImages = images.filter((_, i) => i !== index);
    // Update sort orders
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      sort_order: i,
      // If we removed the primary, make the first one primary
      is_primary: img.is_primary || (images[index].is_primary && i === 0),
    }));
    onChange(reorderedImages);
  };

  const handleSetPrimary = (index: number) => {
    const newImages = images.map((img, i) => ({
      ...img,
      is_primary: i === index,
    }));
    onChange(newImages);
  };

  const handleDragStart = (index: number) => {
    setDraggedIndex(index);
  };

  const handleDragOver = (e: React.DragEvent, index: number) => {
    e.preventDefault();
    if (draggedIndex === null || draggedIndex === index) return;

    // Reorder images
    const newImages = [...images];
    const draggedImage = newImages[draggedIndex];
    newImages.splice(draggedIndex, 1);
    newImages.splice(index, 0, draggedImage);

    // Update sort orders
    const reorderedImages = newImages.map((img, i) => ({
      ...img,
      sort_order: i,
    }));

    onChange(reorderedImages);
    setDraggedIndex(index);
  };

  const handleDragEnd = () => {
    setDraggedIndex(null);
  };

  const canAddMore = images.length + uploadingCount < maxImages;

  return (
    <div className={cn("space-y-3", className)}>
      {/* Image Grid */}
      {(images.length > 0 || uploadingCount > 0) && (
        <div className="grid grid-cols-2 gap-3 sm:grid-cols-3">
          {images.map((image, index) => (
            <div
              key={image.url}
              draggable
              onDragStart={() => handleDragStart(index)}
              onDragOver={(e) => handleDragOver(e, index)}
              onDragEnd={handleDragEnd}
              className={cn(
                "group relative aspect-video cursor-move overflow-hidden rounded-lg border-2 bg-muted",
                draggedIndex === index ? "border-primary opacity-50" : "border-transparent",
                image.is_primary && "ring-2 ring-amber-500 ring-offset-2"
              )}
            >
              <img
                src={image.url}
                alt={`รูปที่ ${index + 1}`}
                className="h-full w-full object-cover"
              />

              {/* Primary badge */}
              {image.is_primary && (
                <div className="absolute left-2 top-2 flex items-center gap-1 rounded-full bg-amber-500 px-2 py-0.5 text-xs font-medium text-white">
                  <Star className="h-3 w-3 fill-current" />
                  หลัก
                </div>
              )}

              {/* Drag handle */}
              <div className="absolute left-2 top-2 flex h-6 w-6 items-center justify-center rounded bg-black/50 text-white opacity-0 transition-opacity group-hover:opacity-100">
                <GripVertical className="h-4 w-4" />
              </div>

              {/* Actions overlay */}
              <div className="absolute inset-0 flex items-center justify-center gap-2 bg-black/50 opacity-0 transition-opacity group-hover:opacity-100">
                {!image.is_primary && (
                  <Button
                    type="button"
                    variant="secondary"
                    size="sm"
                    className="h-8 gap-1 text-xs"
                    onClick={() => handleSetPrimary(index)}
                  >
                    <Star className="h-3 w-3" />
                    ตั้งเป็นหลัก
                  </Button>
                )}
                <Button
                  type="button"
                  variant="destructive"
                  size="sm"
                  className="h-8 w-8 p-0"
                  onClick={() => handleRemove(index)}
                >
                  <Trash2 className="h-4 w-4" />
                </Button>
              </div>
            </div>
          ))}

          {/* Upload placeholders */}
          {Array.from({ length: uploadingCount }).map((_, index) => (
            <div
              key={`uploading-${index}`}
              className="flex aspect-video items-center justify-center rounded-lg border-2 border-dashed border-border bg-muted/50"
            >
              <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
            </div>
          ))}
        </div>
      )}

      {/* Upload Button */}
      {canAddMore && (
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          disabled={uploadingCount > 0}
          className={cn(
            "flex w-full cursor-pointer flex-col items-center justify-center gap-2 rounded-lg border-2 border-dashed border-border bg-muted/50 py-6 transition-colors hover:border-primary hover:bg-muted",
            uploadingCount > 0 && "cursor-not-allowed opacity-50"
          )}
        >
          <ImagePlus className="h-8 w-8 text-muted-foreground" />
          <span className="text-sm font-medium text-muted-foreground">
            {images.length === 0 ? "อัพโหลดรูปรถ" : "เพิ่มรูปเพิ่มเติม"}
          </span>
          <span className="text-xs text-muted-foreground/70">
            {images.length}/{maxImages} รูป • ลากเพื่อจัดเรียง
          </span>
        </button>
      )}

      <input
        ref={fileInputRef}
        type="file"
        accept="image/*"
        multiple
        onChange={handleFileChange}
        className="hidden"
        disabled={uploadingCount > 0}
      />

      {images.length > 0 && (
        <p className="text-xs text-muted-foreground">
          เคล็ดลับ: ลากรูปเพื่อจัดลำดับ • คลิก "ตั้งเป็นหลัก" เพื่อเลือกรูปปกหลัก
        </p>
      )}
    </div>
  );
}
